import { useRef, useState } from "react";
import { toast } from "react-toastify";

import { getChallengeSession, setChallengeSession } from "../api-service/auth";
import { registerFace } from "../api-service/face";
import messages from "../messages/ja.json";
import { useAuth } from "../provider";
import {
  CapturedImage,
  FaceCapture,
  FaceCaptureHandle,
  FaceCaptureUiState,
} from "./face-capture";
import { FaceRegistrationShell } from "./face-registration-shell";

interface Props {
  onDone: () => void;
  onCancel: () => void;
}

const defaultUiState: FaceCaptureUiState = {
  cameraState: "idle",
  captureReady: false,
  hasImage: false,
  modelLoading: false,
};

// Shared face-registration step: capture an image + POST /face-rekognition/register.
export function FaceRegisterStep({ onDone, onCancel }: Props) {
  const auth = useAuth();
  const captureRef = useRef<FaceCaptureHandle>(null);
  const [image, setImage] = useState<CapturedImage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [uiState, setUiState] = useState<FaceCaptureUiState>(defaultUiState);

  const onRegister = async () => {
    if (!image) return;
    const sessionId = getChallengeSession();
    if (!sessionId) {
      const msg = messages.face.registerError;
      setError(msg);
      toast.error(msg);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const nextSession = await registerFace(image.blob, sessionId);
      if (nextSession) setChallengeSession(nextSession);
      auth.setFaceStatus("registered");
      toast.success(messages.face.registerSuccess);
      onDone();
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || messages.face.registerError;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onCaptureClick = () => {
    if (image) {
      void onRegister();
      return;
    }
    captureRef.current?.capture();
  };

  const onRetake = () => {
    setError("");
    setImage(null);
  };

  const captureEnabled =
    Boolean(image) ||
    (uiState.cameraState === "ready" && !uiState.modelLoading && uiState.captureReady);

  // Review state (photo captured): retake (outline) + register (filled) side by side.
  // Live state: cancel (left) + capture (center). Flow unchanged — only the UI differs.
  const footer = image ? (
    <div className="face-review-actions">
      <button
        className="face-review-retake"
        type="button"
        disabled={submitting}
        onClick={onRetake}
      >
        {messages.face.retake}
      </button>

      <button
        className="face-review-register"
        type="button"
        disabled={submitting}
        onClick={onRegister}
      >
        <svg
          className="face-review-register-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 12.5l2.5 2.5 4.5-5" />
        </svg>
        <span>{messages.face.register}</span>
      </button>
    </div>
  ) : (
    <>
      <button
        className="face-registration-cancel"
        type="button"
        disabled={submitting}
        onClick={onCancel}
      >
        {messages.face.cancel}
      </button>

      <button
        className={`face-registration-capture ${captureEnabled ? "is-ready" : ""}`}
        type="button"
        disabled={!captureEnabled || submitting}
        onClick={onCaptureClick}
      >
        <svg
          className="face-registration-capture-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        <span>{messages.face.capture}</span>
      </button>

      <span aria-hidden="true" />
    </>
  );

  const shell = (
    <FaceRegistrationShell username={auth.user?.username} showBanner footer={footer}>
      <FaceCapture
        ref={captureRef}
        value={image}
        onChange={setImage}
        disabled={submitting}
        mode="register"
        loading={submitting}
        onUiStateChange={setUiState}
      />

      {error ? <p className="error-banner face-registration-error">{error}</p> : null}
    </FaceRegistrationShell>
  );

  return (
    <>
      {shell}
      {/* Whole-screen dim behind the panel; the spinner itself sits over the photo
          (rendered inside the review panel, promoted above this dim). */}
      {submitting ? <div className="face-register-overlay" aria-hidden="true" /> : null}
    </>
  );
}
